// @flow

import * as React from 'react';

import { CornerDownRight, X } from 'lucide-react';
import { useDispatch } from 'react-redux';
import { Button, View } from 'reshaped';

import {
  getCart,
  removeProducts,
  selectCartIsActive,
  selectOneProductErrors,
  updateCartProduct,
  type DecoratedProduct,
  type ProductError,
} from '@gandi/cart';
import { notifyDanger } from '@gandi/notifications';
import { selectMailboxesPrices } from '@gandi/redux-resource-price';
import { useTranslator } from '@gandi/translate';
import { Fqdn } from '@gandi/ui';

import { mailboxPriceParams } from 'src/MailboxCreate/modules/actions';
import { useProductsFetch } from 'src/MailboxStandaloneCreate/modules/hooks/useProductsFetch';
import { useAppSelector, useNotifications } from 'src/hooks';

import { ListRow } from '../../ListRow';

import { normalizeMailboxProduct } from '../modules/utils';

import ProductErrorItem from './components/ProductErrorItem';
import DurationSelect from './components/Rows/DurationSelect';
import Price from './components/Rows/Price';

function useMailboxDurations(product: DecoratedProduct): Array<number> {
  const isStandalone = product.productType === 'mailbox-standalone';
  const { mailboxType, duration } = normalizeMailboxProduct(product);

  const { summaries: mbsaCatalog } = useProductsFetch({
    enabled: isStandalone,
  });
  const v2Prices = useAppSelector(state =>
    !isStandalone ? selectMailboxesPrices(state, mailboxPriceParams) : null,
  );

  return React.useMemo(() => {
    if (isStandalone) {
      const catalogProduct = mbsaCatalog?.find(
        p => p.config_name === mailboxType,
      );
      const available = catalogProduct?.prices?.map(p => p.min_duration);
      return available
        ? Array.from(new Set(available))
            .map(Number)
            .sort((a, b) => a - b)
        : [duration];
    }

    const typePrices = v2Prices?.[mailboxType];
    return typePrices
      ? Object.keys(typePrices)
          .map(Number)
          .sort((a, b) => a - b)
      : [duration];
  }, [isStandalone, mbsaCatalog, v2Prices, mailboxType, duration]);
}

const justifyStyle = { s: 'space-between', m: 'end' };

type CTAProps = {
  product: DecoratedProduct,
  active: boolean,
  durations: Array<number>,
  currentDuration: number,
  onUpdateDuration: (option: { value: number, ... }) => Promise<void>,
  onRemove: (e: SyntheticEvent<EventTarget>) => void,
};

function MailboxRenewGroupItemCTA({
  product,
  active,
  durations,
  currentDuration,
  onUpdateDuration,
  onRemove,
}: CTAProps): React.MixedElement {
  const { __ } = useTranslator();
  const inputId = `duration-${product.id}`;

  return (
    <View direction="row" align="center" gap={6} justify={justifyStyle}>
      <DurationSelect
        id={inputId}
        unit="m"
        disabled={!!product.isWorking}
        readOnly={!active}
        values={durations}
        onChange={onUpdateDuration}
        value={currentDuration}
        productId={product.id}
      />

      {/* We force a min width for the price column so "Free" and "71.88 €" align right */}
      <View minWidth={20} justify="end" align="center">
        <Price
          color="light"
          price={
            product.computedTotalPrices?.beforeTaxes ??
            product.total?.before_taxes
          }
          priceCrossedOut={product.total?.before_discount}
        />
      </View>

      <View
        align="center"
        attributes={{
          style: {
            visibility: active && !product.isWorking ? 'visible' : 'hidden',
          },
        }}
      >
        <Button
          icon={X}
          variant="ghost"
          attributes={{ title: __('Remove') }}
          onClick={onRemove}
        />
      </View>
    </View>
  );
}

type Props = {
  product: DecoratedProduct,
};

export function MailboxRenewGroupItem({ product }: Props): React.MixedElement {
  const dispatch = useDispatch();
  const { __, formatDate } = useTranslator();
  const { notifyMaybeApiError } = useNotifications();

  const active = useAppSelector(selectCartIsActive);
  const productErrors: Array<ProductError> = useAppSelector(state =>
    selectOneProductErrors(state, {
      productId: product.id,
    }),
  );

  const { duration, email } = normalizeMailboxProduct(product);
  const durations = useMailboxDurations(product);

  const expirationDate = product.expirationDate;
  const info = expirationDate
    ? __('Expires on %(dateEnd)s', {
        dateEnd: formatDate(expirationDate, 'LL'),
      })
    : undefined;

  const handleUpdateDuration = async (option: {
    value: number,
    ...
  }): Promise<void> => {
    try {
      await dispatch(
        updateCartProduct({
          productId: product.id,
          payload: { ...product.payload, duration: option.value },
        }),
      );
      await dispatch(getCart());
    } catch (e) {
      notifyMaybeApiError(e);
    }
  };

  const handleRemove = (e: SyntheticEvent<EventTarget>): void => {
    e.preventDefault();

    dispatch(removeProducts([{ id: product.id }]))
      .then(() => dispatch(getCart()))
      .catch(() => {
        notifyDanger({
          message: __('An error has occurred. Please try again.'),
        });
      });
  };

  return (
    <>
      {productErrors.length > 0 && (
        <ProductErrorItem errors={productErrors} productId={product.id} />
      )}
      <ListRow
        intent="muted"
        icon={<CornerDownRight size={16} />}
        title={
          <Fqdn color="base" overflow="break">
            {email}
          </Fqdn>
        }
        options={info ? [info] : undefined}
        optionsArrow={false}
        cta={
          <MailboxRenewGroupItemCTA
            product={product}
            active={active}
            durations={durations}
            currentDuration={duration}
            onUpdateDuration={handleUpdateDuration}
            onRemove={handleRemove}
          />
        }
      />
    </>
  );
}
